import { Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { Gender } from '@portal/models/enumerators/loans';
import LoanApi from '@portal/repositories/loan';

import { LoadingActions, UserActions } from '.';
import {
  SET_LOAN,
  SET_LOANS,
  SET_LOAN_ERROR,
  SET_LOAN_EXTERNAL_SIMULATION,
  SET_LOAN_SIMULATION,
  SET_LOAN_SUCCESS,
  SET_PRE_APPROVED_LOANS,
} from './actionTypes';
import { LoadingAction } from './loading';
import { UpdateAccountAction } from './user';

export type SetLoanErrorAction = {
  type: string;
  payload: Error | null;
};

export type SetLoanSuccessAction = {
  type: string;
  payload: string | null;
};

export const setLoanError =
  (payload: SetLoanErrorAction['payload']) =>
  (dispatch: Dispatch<SetLoanErrorAction | SetLoanSuccessAction>) => {
    dispatch({
      type: SET_LOAN_ERROR,
      payload,
    });
    dispatch({
      type: SET_LOAN_SUCCESS,
      payload: null,
    });
  };

export const setLoanSuccess =
  (payload: SetLoanSuccessAction['payload']) =>
  (dispatch: Dispatch<SetLoanErrorAction | SetLoanSuccessAction>) => {
    dispatch({
      type: SET_LOAN_ERROR,
      payload: null,
    });
    dispatch({
      type: SET_LOAN_SUCCESS,
      payload,
    });
  };

export type SetPreApprovedLoansAction = {
  type: string;
  payload: models.PreApprovedLoan[] | null;
};

export const setPreApprovedLoans = (
  payload: SetPreApprovedLoansAction['payload']
) => ({
  type: SET_PRE_APPROVED_LOANS,
  payload,
});

export const getPreApprovedLoans =
  (
    load = true
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetPreApprovedLoansAction | SetLoanErrorAction
  > =>
  async (dispatch) => {
    if (load) {
      dispatch(LoadingActions.increaseLoading());
    }
    try {
      const preApprovedLoans = await LoanApi.getPreApproved();

      dispatch(setPreApprovedLoans(preApprovedLoans));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setLoanError(err));
      }
    } finally {
      if (load) {
        dispatch(LoadingActions.decreaseLoading());
      }
    }
  };

export type SetLoansAction = {
  type: string;
  payload: bank.Loan[];
};

export const setLoans = (payload: SetLoansAction['payload']) => ({
  type: SET_LOANS,
  payload,
});

export const getLoans =
  (
    load = true
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetLoansAction | SetLoanErrorAction
  > =>
  async (dispatch) => {
    if (load) {
      dispatch(LoadingActions.increaseLoading());
    }
    try {
      const loans = await LoanApi.getLoans();

      dispatch(setLoans(loans));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setLoanError(err));
      }
    } finally {
      if (load) {
        dispatch(LoadingActions.decreaseLoading());
      }
    }
  };

export type SetLoanSimulationAction = {
  type: string;
  payload: models.LoanSimulation[] | null;
};

export const setLoanSimulation = (
  payload: SetLoanSimulationAction['payload']
) => ({
  type: SET_LOAN_SIMULATION,
  payload,
});

export const getSimulation =
  (
    id: string,
    amount: number
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetLoanSimulationAction | SetLoanErrorAction
  > =>
  async (dispatch) => {
    dispatch(LoadingActions.increaseLoading());
    try {
      const simulations = await LoanApi.getSimulation(id, amount);

      dispatch(setLoanSimulation(simulations));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setLoanError(err));
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };

export type SetLoanExternalSimulationAction = {
  type: string;
  payload: models.LoanExternalSimulation | null;
};

export const setLoanExternalSimulation = (
  payload: SetLoanExternalSimulationAction['payload']
) => ({
  type: SET_LOAN_EXTERNAL_SIMULATION,
  payload,
});

export const getExternalSimulation =
  (
    id: string,
    simulation: models.ExternalSimulationRequest
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetLoanExternalSimulationAction | SetLoanErrorAction
  > =>
  async (dispatch) => {
    dispatch(LoadingActions.increaseLoading());
    try {
      const externalSimulation = await LoanApi.getExternalSimulation(
        id,
        simulation
      );

      dispatch(setLoanExternalSimulation(externalSimulation));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setLoanError(err));
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };

export type SetLoanAction = {
  type: string;
  payload: bank.Loan | null;
};

export const setLoan = (payload: SetLoanAction['payload']) => ({
  type: SET_LOAN,
  payload,
});

export const createLoan =
  (
    preApprovedLoanId: string,
    simulation: models.LoanExternalSimulation,
    LoanBankAccountData: models.LoanBankAccountData
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetLoanSuccessAction | SetLoanErrorAction | SetLoanAction
  > =>
  async (dispatch) => {
    dispatch(LoadingActions.increaseLoading());
    try {
      const loan = await LoanApi.createLoan(
        preApprovedLoanId,
        simulation,
        LoanBankAccountData
      );

      dispatch(setLoanSuccess('CREATE'));
      dispatch(setLoan(loan));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setLoanError(err));
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };

export const updateAccount =
  (newData: {
    gender?: Gender;
    businessLine?: number;
    financialStatistic?: number;
  }): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    | LoadingAction
    | SetLoanSuccessAction
    | SetLoanErrorAction
    | UpdateAccountAction
  > =>
  async (dispatch) => {
    dispatch(LoadingActions.increaseLoading());
    try {
      const modifiedGender = newData.gender === Gender.FEMALE ? 'F' : 'M';
      await LoanApi.updateAccount({
        ...newData,
        gender: newData.gender ? modifiedGender : undefined,
      });
      dispatch(UserActions.getMe());

      dispatch(setLoanSuccess('ACCOUNT_UPDATED'));
      dispatch(UserActions.updateAccount('gender', modifiedGender));
      dispatch(UserActions.updateAccount('businessLine', newData.businessLine));
      dispatch(
        UserActions.updateAccount(
          'financialStatistic',
          newData.financialStatistic
        )
      );
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setLoanError(err));
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };

export const uploadWarranties =
  (
    loanId: string,
    warranties: FileList
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    | LoadingAction
    | SetLoanSuccessAction
    | SetLoanErrorAction
    | UpdateAccountAction
  > =>
  async (dispatch) => {
    dispatch(LoadingActions.increaseLoading());
    try {
      for (let i = 0; i < warranties.length; i++) {
        const warranty = warranties.item(i);

        if (!warranty) {
          return;
        }

        const { url } = await LoanApi.getWarrantyUploadUrl(
          loanId,
          warranty.name.split('.')[1]
        );
        const buffer = await warranty.arrayBuffer();
        await LoanApi.uploadWarranty(url, Buffer.from(buffer));
      }
      await LoanApi.requestAnalyze(loanId);
      dispatch(setLoanSuccess('LOAN_FINALIZE'));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setLoanError(err));
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };

export const saveLoanIntention =
  (
    type: number
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetLoanSuccessAction | SetLoanErrorAction | SetLoanAction
  > =>
  async (dispatch) => {
    dispatch(LoadingActions.increaseLoading());
    try {
      await LoanApi.saveLoanIntention(type);
      dispatch(setLoanSuccess('LOAN_INTENTION'));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setLoanError(err));
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };
